import { useEffect, useRef, useState } from 'react';
import {
  BellRing, Chrome, Download, MonitorDown, MoreVertical, Share, Smartphone, SquarePlus, X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  SHOW_DELAY_MS,
  getInstallPlatform,
  isIos,
  isMobileDevice,
  isStandalone,
  promptInstall,
  pushPwaEvent,
  snoozeInstallPrompt,
  subscribeInstall,
  wasRecentlyDismissed,
} from '@/lib/pwaInstall';

/**
 * iOS Safari has no install API — this dialog walks the user through
 * Share → Add to Home Screen. Shared by the banner and InstallAppLink.
 */
export function IosInstallGuide({ open, onOpenChange, onDone, testIdPrefix = 'pwa' }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid={`${testIdPrefix}-install-ios-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-display">Install RotaHQ on your iPhone</DialogTitle>
          <DialogDescription>Takes about 10 seconds in Safari:</DialogDescription>
        </DialogHeader>
        <ol className="space-y-3 text-sm">
          <li className="flex items-start gap-3">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[color:var(--brand-primary-soft)] text-[color:var(--brand-primary)]">
              <Share className="h-4 w-4" />
            </span>
            <span className="pt-1">
              Tap the <span className="font-semibold">Share</span> button in Safari&apos;s toolbar.
            </span>
          </li>
          <li className="flex items-start gap-3">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[color:var(--brand-primary-soft)] text-[color:var(--brand-primary)]">
              <SquarePlus className="h-4 w-4" />
            </span>
            <span className="pt-1">
              Scroll down, tap <span className="font-semibold">&ldquo;Add to Home Screen&rdquo;</span>, then tap <span className="font-semibold">Add</span>.
            </span>
          </li>
          <li className="flex items-start gap-3">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[color:var(--brand-primary-soft)] text-[color:var(--brand-primary)]">
              <BellRing className="h-4 w-4" />
            </span>
            <span className="pt-1">
              Open RotaHQ from your home screen and switch on push alerts from the notification bell.
            </span>
          </li>
        </ol>
        <Button
          onClick={() => {
            onOpenChange(false);
            onDone?.();
          }}
          className="w-full bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-white"
          data-testid={`${testIdPrefix}-install-ios-done`}
        >
          Got it
        </Button>
      </DialogContent>
    </Dialog>
  );
}

/** Per-browser manual install steps (used when the native prompt is
 *  unavailable: engagement heuristics not met, native prompt previously
 *  dismissed, or browsers without `beforeinstallprompt`). */
const MANUAL_STEPS = {
  'chrome-desktop': {
    icon: MonitorDown,
    steps: [
      'Look for the install icon at the right end of the address bar and click it.',
      'No icon? Open the \u22EE menu \u2192 "Cast, save and share" \u2192 "Install page as app".',
    ],
  },
  'edge-desktop': {
    icon: MonitorDown,
    steps: [
      'Open the \u2026 menu in the top-right corner.',
      'Choose "Apps" \u2192 "Install this site as an app".',
    ],
  },
  'safari-mac': {
    icon: SquarePlus,
    steps: [
      'In the Safari menu bar, choose File \u2192 "Add to Dock\u2026".',
      'Click "Add" \u2014 RotaHQ opens as its own app from your Dock.',
    ],
  },
  'firefox-desktop': {
    icon: Chrome,
    steps: [
      'Firefox doesn\u2019t support installing web apps on desktop.',
      'Open rotahq.uk in Chrome or Edge and click Install there.',
    ],
  },
  'android-chromium': {
    icon: MoreVertical,
    steps: [
      'Tap the \u22EE menu in the top-right corner of your browser.',
      'Tap "Add to Home screen" (or "Install app"), then confirm with Install.',
    ],
  },
  'android-other': {
    icon: MoreVertical,
    steps: [
      'Open your browser menu.',
      'Choose "Add to Home screen" \u2014 or open RotaHQ in Chrome and tap Install.',
    ],
  },
  generic: {
    icon: Download,
    steps: [
      'Open your browser menu.',
      'Choose "Install app" or "Add to Home screen".',
    ],
  },
};

/** Browser-specific walkthrough for installing without the native prompt. */
export function ManualInstallGuide({ open, onOpenChange, onDone, testIdPrefix = 'pwa' }) {
  const platform = getInstallPlatform();
  const guide = MANUAL_STEPS[platform] || MANUAL_STEPS.generic;
  const StepIcon = guide.icon;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid={`${testIdPrefix}-install-manual-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-display">Install RotaHQ</DialogTitle>
          <DialogDescription>Two quick steps in your browser:</DialogDescription>
        </DialogHeader>
        <ol className="space-y-3 text-sm">
          {guide.steps.map((step, i) => (
            <li key={step} className="flex items-start gap-3">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[color:var(--brand-primary-soft)] text-[color:var(--brand-primary)]">
                {i === 0 ? <StepIcon className="h-4 w-4" /> : <Download className="h-4 w-4" />}
              </span>
              <span className="pt-1">{step}</span>
            </li>
          ))}
        </ol>
        <Button
          onClick={() => {
            onOpenChange(false);
            onDone?.();
          }}
          className="w-full bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-white"
          data-testid={`${testIdPrefix}-install-manual-done`}
        >
          Got it
        </Button>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Floating, dismissible "Install RotaHQ" banner.
 *
 * Mounted on the marketing landing page, both login pages and both
 * logged-in shells. ALWAYS appears ~5s after page load (unless installed,
 * running standalone, or snoozed) so the install feature is visible on
 * every browser:
 *
 * - Native path: when Chromium fired `beforeinstallprompt` (captured
 *   globally in lib/pwaInstall.js before React mounts), Install opens the
 *   real OS install sheet.
 * - iOS Safari: Install opens the Share → Add to Home Screen guide.
 * - Everything else (event not fired / unsupported browser): Install opens
 *   browser-specific manual steps.
 *
 * "Not now" / close snoozes for 7 days. Funnel events go to the GTM
 * dataLayer only.
 */
export function InstallPrompt({ testIdPrefix = 'pwa' }) {
  const [visible, setVisible] = useState(false);
  const [canPrompt, setCanPrompt] = useState(false);
  const [iosHelpOpen, setIosHelpOpen] = useState(false);
  const [manualHelpOpen, setManualHelpOpen] = useState(false);
  const shownTrackedRef = useRef(false);
  const installedToastRef = useRef(false);

  useEffect(() => {
    if (isStandalone()) return undefined; // already running as an app
    if (wasRecentlyDismissed()) return undefined;

    // Universal 5s timer — the banner no longer depends on the browser
    // firing beforeinstallprompt (that event is unreliable: engagement
    // heuristics, previously dismissed native prompts, non-Chromium).
    const timer = setTimeout(() => setVisible(true), SHOW_DELAY_MS);

    const unsubscribe = subscribeInstall(({ canPrompt: available, installed }) => {
      if (installed) {
        setVisible(false);
        if (!installedToastRef.current) {
          installedToastRef.current = true;
          toast.success('RotaHQ installed — find it on your home screen', {
            description: 'Open it there and switch on push alerts from the notification bell.',
          });
        }
        return;
      }
      setCanPrompt(available);
    });

    return () => {
      unsubscribe();
      clearTimeout(timer);
    };
  }, []);

  // Agency funnel: count one impression per page load.
  useEffect(() => {
    if (visible && !shownTrackedRef.current) {
      shownTrackedRef.current = true;
      pushPwaEvent('pwa_install_prompt_shown');
    }
  }, [visible]);

  const dismiss = () => {
    snoozeInstallPrompt();
    pushPwaEvent('pwa_install_dismissed');
    setVisible(false);
  };

  const install = async () => {
    if (canPrompt) {
      // promptInstall() pushes pwa_install_click and opens the OS sheet.
      const outcome = await promptInstall();
      if (outcome !== 'accepted') snoozeInstallPrompt();
      setVisible(false);
      return;
    }
    pushPwaEvent('pwa_install_click');
    if (isIos()) setIosHelpOpen(true);
    else setManualHelpOpen(true);
  };

  if (!visible) return null;

  const mobile = isMobileDevice();

  return (
    <>
      <div
        role="dialog"
        aria-label="Install RotaHQ"
        data-testid={`${testIdPrefix}-install-prompt`}
        className="fixed z-50 left-1/2 -translate-x-1/2 bottom-5 w-[calc(100%-2rem)] max-w-md sm:left-auto sm:translate-x-0 sm:bottom-auto sm:top-20 sm:right-5 sm:w-96"
      >
        <div className="surface-card shadow-xl border border-border p-4 sm:p-5 flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[color:var(--brand-primary-soft)] text-[color:var(--brand-primary)]">
            <Smartphone className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-sm font-semibold leading-tight">
              {mobile ? 'Install RotaHQ on your phone' : 'Install RotaHQ'}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {mobile
                ? 'Get a fast, full-screen app icon on your home screen. Works offline for everything except live clock-ins.'
                : 'Get a fast, full-screen RotaHQ app on your desktop. Works offline for everything except live clock-ins.'}
            </p>
            <div className="flex gap-2 mt-3">
              <Button
                size="sm"
                onClick={install}
                className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-white"
                data-testid={`${testIdPrefix}-install-confirm`}
              >
                <Download className="h-3.5 w-3.5 mr-1.5" /> Install
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={dismiss}
                className="text-muted-foreground hover:text-foreground"
                data-testid={`${testIdPrefix}-install-later`}
              >
                Not now
              </Button>
            </div>
          </div>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss install prompt"
            data-testid={`${testIdPrefix}-install-close`}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      <IosInstallGuide
        open={iosHelpOpen}
        onOpenChange={setIosHelpOpen}
        onDone={dismiss}
        testIdPrefix={testIdPrefix}
      />
      <ManualInstallGuide
        open={manualHelpOpen}
        onOpenChange={setManualHelpOpen}
        onDone={dismiss}
        testIdPrefix={testIdPrefix}
      />
    </>
  );
}

/**
 * Permanent "Install app" entry point (marketing footer + app menus) so the
 * install flow can be re-triggered any time, on every browser.
 *
 * - Chromium with a stored prompt → real OS install sheet.
 * - iOS Safari → Add-to-Home-Screen guide.
 * - Everything else → browser-specific manual steps.
 * - Hidden only when already running as the installed app.
 */
export function InstallAppLink({ className = '', testId = 'install-app-link' }) {
  const [state, setState] = useState({ canPrompt: false, installed: false });
  const [iosOpen, setIosOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);

  useEffect(() => subscribeInstall(setState), []);

  if (isStandalone() || state.installed) return null;

  const onClick = async () => {
    if (state.canPrompt) {
      await promptInstall();
      return;
    }
    pushPwaEvent('pwa_install_click');
    if (isIos()) setIosOpen(true);
    else setManualOpen(true);
  };

  return (
    <>
      <button
        type="button"
        onClick={onClick}
        data-testid={testId}
        className={
          className ||
          'inline-flex items-center gap-1.5 hover:text-[color:var(--brand-primary)] transition-colors duration-150'
        }
      >
        <Download className="h-3.5 w-3.5" aria-hidden="true" />
        Install app
      </button>
      <IosInstallGuide open={iosOpen} onOpenChange={setIosOpen} testIdPrefix={testId} />
      <ManualInstallGuide open={manualOpen} onOpenChange={setManualOpen} testIdPrefix={testId} />
    </>
  );
}

export default InstallPrompt;
